import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
  Button
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbar
} from '@mui/x-data-grid';
import { Home, Dashboard, Refresh } from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { LlmActivityDetailDialog } from '../../components/admin/LlmActivityDetailDialog';

interface LlmActivityRecord {
  id: number;
  llm_model_id: number;
  model_name: string;
  provider: string;
  system_prompt: string | null;
  prompt: string;
  response_body: string | null;
  error_message: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  duration_ms: number | null;
  estimated_cost_usd: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const LlmActivityPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [selectedRecord, setSelectedRecord] = useState<LlmActivityRecord | null>(null);

  // Fetch LLM activity logs
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['llm-activity'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/developer-tools/llm-activity`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch LLM activity logs');
      }
      const result = await response.json();
      return result.data as LlmActivityRecord[];
    }
  });

  const handleRefresh = () => {
    refetch();
  };

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have developer_tools.view permission
  if (!user?.permissions?.includes('developer_tools.view')) {
    return <Navigate to="/" replace />;
  }

  const truncateText = (text: string | null, maxLength: number = 50): string => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueFormatter: (value) => {
        const date = new Date(value);
        return date.toLocaleString();
      }
    },
    {
      field: 'model_name',
      headerName: 'Model',
      width: 180
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'completed' ? 'success' :
            params.value === 'failed' ? 'error' :
            'default'
          }
          size="small"
        />
      )
    },
    {
      field: 'prompt',
      headerName: 'Prompt (first 50 chars)',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {truncateText(params.value, 50)}
        </Typography>
      )
    },
    {
      field: 'response_body',
      headerName: 'Response (first 50 chars)',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {truncateText(params.value, 50)}
        </Typography>
      )
    },
    {
      field: 'total_tokens',
      headerName: 'Tokens',
      width: 100,
      valueFormatter: (value: number | null | undefined) => value?.toLocaleString() || '-'
    },
    {
      field: 'estimated_cost_usd',
      headerName: 'Cost',
      width: 100,
      valueFormatter: (value: number | null | undefined) => value ? `$${value.toFixed(6)}` : '-'
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 100,
      valueFormatter: (value: number | null | undefined) => value ? `${value}ms` : '-'
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">LLM Activity</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Dashboard sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                LLM Activity Logs
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
              onClick={handleRefresh}
              disabled={isFetching}
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            View and analyze LLM API call logs including prompts, responses, and usage metrics
          </Typography>
        </Box>

        {/* DataGrid */}
        <Box sx={{ height: 700, width: '100%' }}>
          <DataGrid
            rows={data || []}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            slots={{
              toolbar: GridToolbar
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 }
              },
              sorting: {
                sortModel: [{ field: 'created_at', sort: 'desc' }]
              }
            }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedRecord(params.row as LlmActivityRecord)}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer'
              }
            }}
          />
        </Box>

        {/* Detail Dialog */}
        {selectedRecord && (
          <LlmActivityDetailDialog
            record={selectedRecord}
            open={!!selectedRecord}
            onClose={() => setSelectedRecord(null)}
          />
        )}
      </Box>
    </Container>
  );
};
